import React from 'react';
import { Box, Chip, makeStyles } from '@material-ui/core';
import CodeIcon from '@material-ui/icons/Code';
import ExtensionIcon from '@material-ui/icons/Extension';
import LanguageIcon from '@material-ui/icons/Language';
import GroupIcon from '@material-ui/icons/Group';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import StorageIcon from '@material-ui/icons/Storage';
import AppsIcon from '@material-ui/icons/Apps';
import PersonIcon from '@material-ui/icons/Person';
import {
  EntityKindFilter,
  useEntityList,
} from '@backstage/plugin-catalog-react';

type KindConfig = {
  label: string;
  color: string;
  icon: React.ReactElement;
};

const KIND_CONFIG: Record<string, KindConfig> = {
  api:       { label: 'API',        color: '#e53935', icon: <CodeIcon style={{ fontSize: 16 }} /> },
  component: { label: 'Components', color: '#43a047', icon: <ExtensionIcon style={{ fontSize: 16 }} /> },
  domain:    { label: 'Domain',     color: '#5c6bc0', icon: <LanguageIcon style={{ fontSize: 16 }} /> },
  group:     { label: 'Group',      color: '#00897b', icon: <GroupIcon style={{ fontSize: 16 }} /> },
  location:  { label: 'Location',   color: '#fb8c00', icon: <LocationOnIcon style={{ fontSize: 16 }} /> },
  resource:  { label: 'Resources',  color: '#8e24aa', icon: <StorageIcon style={{ fontSize: 16 }} /> },
  system:    { label: 'System',     color: '#1e88e5', icon: <AppsIcon style={{ fontSize: 16 }} /> },
  user:      { label: 'Users',      color: '#f9a825', icon: <PersonIcon style={{ fontSize: 16 }} /> },
};

const AVAILABLE_ENTITY_KINDS = [
  'api',
  'component',
  'domain',
  'group',
  'location',
  'resource',
  'system',
  'user',
] as const;

/** Wraps the icon in a dark pill so it stays visible on the solid selected background. */
const IconBadge = ({ color, icon, isSelected }: { color: string; icon: React.ReactElement; isSelected: boolean }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: 6,
      flexShrink: 0,
    }}
  >
    {React.cloneElement(icon, { style: { color: isSelected ? '#fff' : color, fontSize: 18 } })}
  </span>
);

const useStyles = makeStyles(theme => ({
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  chipLabel: {
    paddingLeft: 10,
    paddingRight: 14,
    fontSize: '0.85rem',
  },
  chipIcon: {
    marginLeft: '0 !important',
    marginRight: 0,
  },
}));

export const CustomEntityKindPicker: React.FC = () => {
  const classes = useStyles();
  const {
    filters,
    updateFilters,
    queryParameters: { kind: kindFromUrl },
  } = useEntityList();

  const selectedKind = filters.kind?.value || (kindFromUrl as string) || 'component';

  React.useEffect(() => {
    if (!filters.kind && !kindFromUrl) {
      updateFilters({ kind: new EntityKindFilter('component', 'Components') });
    }
  }, [filters.kind, kindFromUrl, updateFilters]);

  const handleKindClick = (kind: string) => {
    const config = KIND_CONFIG[kind];
    updateFilters({
      kind: selectedKind === kind ? undefined : new EntityKindFilter(kind, config.label),
    });
  };

  return (
    <Box className={classes.chipRow}>
      {AVAILABLE_ENTITY_KINDS.map(kind => {
        const config = KIND_CONFIG[kind];
        const isSelected = selectedKind === kind;
        return (
          <Chip
            key={kind}
            label={config.label}
            classes={{ label: classes.chipLabel, icon: classes.chipIcon }}
            icon={<IconBadge color={config.color} icon={config.icon} isSelected={isSelected} />}
            onClick={() => handleKindClick(kind)}
            style={{
              backgroundColor: isSelected ? config.color : `${config.color}40`,
              color: '#fff',
              borderRadius: 20,
              fontWeight: isSelected ? 700 : 500,
              border: `1px solid ${config.color}`,
              cursor: 'pointer',
              paddingLeft: 2,
              height: 36,
            }}
          />
        );
      })}
    </Box>
  );
};